import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { useTranslation } from 'react-i18next';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const apiClient = useApiClient();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!token) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-neutral-900">{t('auth.resetPassword.invalidLinkTitle')}</h2>
        <p className="text-sm text-neutral-600">{t('auth.resetPassword.invalidLinkDescription')}</p>
        <Link to="/forgot-password" className="inline-block mt-4 px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors">
          {t('auth.resetPassword.requestNewLink')}
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-neutral-900">{t('auth.resetPassword.doneTitle')}</h2>
        <p className="text-sm text-neutral-600">{t('auth.resetPassword.doneDescription')}</p>
        <button
          onClick={() => navigate('/login')}
          className="inline-block mt-4 px-6 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
        >
          {t('auth.resetPassword.loginLink')}
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (newPassword.length < 8) {
      setErrors({ newPassword: t('auth.resetPassword.passwordMin') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: t('auth.resetPassword.passwordMismatch') });
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.resetPassword(token, newPassword);
      setDone(true);
      addToast(t('auth.resetPassword.successToast'), 'success');
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, t('auth.resetPassword.errorToast'));
      setErrors({ submit: errorMessage });
      addToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-neutral-900">{t('auth.resetPassword.title')}</h2>
        <p className="text-sm text-neutral-600 mt-2">{t('auth.resetPassword.description')}</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          {t('auth.resetPassword.newPassword')}
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="••••••••"
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${errors.newPassword ? 'border-error focus:ring-error' : 'border-neutral-300 focus:ring-primary-500'}`}
          minLength={8}
          required
        />
        {errors.newPassword && <p className="mt-1 text-sm text-error">{errors.newPassword}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          {t('auth.resetPassword.confirmPassword')}
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${errors.confirmPassword ? 'border-error focus:ring-error' : 'border-neutral-300 focus:ring-primary-500'}`}
          minLength={8}
          required
        />
        {errors.confirmPassword && <p className="mt-1 text-sm text-error">{errors.confirmPassword}</p>}
      </div>

      {errors.submit && (
        <div className="p-3 bg-error/10 border border-error text-error rounded-lg text-sm">
          {errors.submit}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? t('auth.resetPassword.loading') : t('auth.resetPassword.submit')}
      </button>

      <p className="text-center text-sm text-neutral-600">
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
          {t('auth.backToLogin')}
        </Link>
      </p>
    </form>
  );
}
